import {
  createSlice,
  createEntityAdapter,
  PayloadAction,
  createSelector,
} from '@reduxjs/toolkit';
import { ProjectStage } from 'api/projectProcess';
import { RootState } from 'state/store';
import { selectProcessById } from './processesSlice';
import { Status } from 'utils/customTypes';

const stagesAdapter = createEntityAdapter<ProjectStage>();

interface StagesState {
  status?: Status;
  selectedStageIds?: string[];
}

const initialState: StagesState = {};

const stagesSlice = createSlice({
  name: 'projectsList/filters/stages',
  initialState: stagesAdapter.getInitialState(initialState),
  reducers: {
    upsertStages: (state, action: PayloadAction<ProjectStage[]>) => {
      stagesAdapter.upsertMany(state, action.payload);
    },
    setSelectedStageIds: (state, action) => {
      state.selectedStageIds = action.payload;
    },
  },
});

export const { upsertStages, setSelectedStageIds } = stagesSlice.actions;

const toStage = (stage: ProjectStage) => {
  return {
    id: stage.id,
    name: stage.stageName,
    processId: stage.process_id,
  };
};

const getStagesState = (state: RootState) => state.projectsList.filters.stages;

const { selectAll: selectAllStages } = stagesAdapter.getSelectors(
  (state: RootState) => getStagesState(state)
);

export const selectStages = createSelector(
  [selectAllStages, (state: RootState) => state],
  (stages, state) => {
    if (!stages) {
      return [];
    }
    return stages.map((stage: ProjectStage) => {
      const process = selectProcessById(state, stage.process_id);
      return {
        ...toStage(stage),
        processName: process?.processName || '',
      };
    });
  }
);

export const selectStagesByProcessId = createSelector(
  [selectAllStages, (state, processId: string) => processId],
  (stages, processId) => {
    return stages
      .filter((stage) => stage.process_id === processId)
      .map(toStage);
  }
);

export const selectSelectedStageIds = (state: RootState) =>
  getStagesState(state).selectedStageIds || [];

export const selectSelectedStages = createSelector(
  [selectStages, selectSelectedStageIds],
  (stages, selectedStageIds) => {
    return stages.filter((stage) => selectedStageIds.includes(stage.id));
  }
);

export default stagesSlice.reducer;
